import { lazy } from 'react';

import { createBrowserRouter, Navigate } from 'react-router-dom';

import { AdminLayout } from '~modules/admin/components/admin-layout';

import { LessorLayout } from '~components/layout/lessor-layout';
import { TenantLayout } from '~components/layout/tenant-layout';

import { NotFoundPage } from '~app/pages/lessor/not-found/not-found-page';
import { AdminPrivateRoute } from '~app/router/accessors/admin-private-route';
import { BaseRoute } from '~app/router/accessors/base-route';
import { LessorGuestOnlyRoute } from '~app/router/accessors/lessor-guest-only-route';
import { LessorOnboardingRoute } from '~app/router/accessors/lessor-onboarding-route';
import { LessorPrivateRoute } from '~app/router/accessors/lessor-private-route';
import { TenantGuestOnlyRoute } from '~app/router/accessors/tenant-guest-only-route';
import { TenantPrivateRoute } from '~app/router/accessors/tenant-private-route';
import { RouteErrorPage } from '~app/router/components/route-error-page';

import { ROUTES } from './routes';

const ForgotPasswordPage = lazy(() =>
  import('~app/pages/lessor/auth/forgot-password/forgot-password-page').then(
    (m) => ({
      default: m.ForgotPasswordPage,
    }),
  ),
);

const LoginPage = lazy(() =>
  import('~app/pages/lessor/auth/login/login-page').then((m) => ({
    default: m.LoginPage,
  })),
);

const OAuthSuccessPage = lazy(() =>
  import('~app/pages/lessor/auth/oauth-success/oauth-success-page').then(
    (m) => ({
      default: m.OAuthSuccessPage,
    }),
  ),
);

const ResetPasswordPage = lazy(() =>
  import('~app/pages/lessor/auth/reset-password/reset-password-page').then(
    (m) => ({
      default: m.ResetPasswordPage,
    }),
  ),
);

const SignupPage = lazy(() =>
  import('~app/pages/lessor/auth/signup/signup-page').then((m) => ({
    default: m.SignupPage,
  })),
);

const VerifyEmailPage = lazy(() =>
  import('~app/pages/lessor/auth/verify-email/verify-email-page').then((m) => ({
    default: m.VerifyEmailPage,
  })),
);

const DashboardPage = lazy(() =>
  import('~app/pages/lessor/dashboard/dashboard-page').then((m) => ({
    default: m.DashboardPage,
  })),
);

const LessorOnboardingPage = lazy(() =>
  import('~app/pages/lessor/onboarding/lessor-onboarding-page').then((m) => ({
    default: m.LessorOnboardingPage,
  })),
);

const InvoiceAddPage = lazy(() =>
  import('~app/pages/lessor/invoices/add/invoice-add-page').then((m) => ({
    default: m.InvoiceAddPage,
  })),
);

const InvoiceEditPage = lazy(() =>
  import('~app/pages/lessor/invoices/edit/invoice-edit-page').then((m) => ({
    default: m.InvoiceEditPage,
  })),
);

const InvoiceItemPage = lazy(() =>
  import('~app/pages/lessor/invoices/item/invoice-item-page').then((m) => ({
    default: m.InvoiceItemPage,
  })),
);

const InvoicesListPage = lazy(() =>
  import('~app/pages/lessor/invoices/list/invoices-list-page').then((m) => ({
    default: m.InvoicesListPage,
  })),
);

const LeaseAddPage = lazy(() =>
  import('~app/pages/lessor/leases/add/lease-add-page').then((m) => ({
    default: m.LeaseAddPage,
  })),
);

const LeaseEditPage = lazy(() =>
  import('~app/pages/lessor/leases/edit/lease-edit-page').then((m) => ({
    default: m.LeaseEditPage,
  })),
);

const LeaseItemPage = lazy(() =>
  import('~app/pages/lessor/leases/item/lease-item-page').then((m) => ({
    default: m.LeaseItemPage,
  })),
);

const LeaseItemCreatedPage = lazy(() =>
  import('~app/pages/lessor/leases/item-created/lease-item-created-page').then(
    (m) => ({
      default: m.LeaseItemCreatedPage,
    }),
  ),
);

const LeasesListPage = lazy(() =>
  import('~app/pages/lessor/leases/list/leases-list-page').then((m) => ({
    default: m.LeasesListPage,
  })),
);

const PaymentRecordAddPage = lazy(() =>
  import('~app/pages/lessor/payments-records/add/payment-record-add-page').then(
    (m) => ({
      default: m.PaymentRecordAddPage,
    }),
  ),
);

const PaymentRecordEditPage = lazy(() =>
  import(
    '~app/pages/lessor/payments-records/edit/payment-record-edit-page'
  ).then((m) => ({
    default: m.PaymentRecordEditPage,
  })),
);

const PaymentRecordItemPage = lazy(() =>
  import(
    '~app/pages/lessor/payments-records/item/payment-record-item-page'
  ).then((m) => ({
    default: m.PaymentRecordItemPage,
  })),
);

const PaymentsRecordsListPage = lazy(() =>
  import(
    '~app/pages/lessor/payments-records/list/payments-records-list-page'
  ).then((m) => ({
    default: m.PaymentsRecordsListPage,
  })),
);

const PropertyAddPage = lazy(() =>
  import('~app/pages/lessor/properties/add/property-add-page').then((m) => ({
    default: m.PropertyAddPage,
  })),
);

const PropertyEditPage = lazy(() =>
  import('~app/pages/lessor/properties/edit/property-edit-page').then((m) => ({
    default: m.PropertyEditPage,
  })),
);

const PropertyItemPage = lazy(() =>
  import('~app/pages/lessor/properties/item/property-item-page').then((m) => ({
    default: m.PropertyItemPage,
  })),
);

const PropertiesListPage = lazy(() =>
  import('~app/pages/lessor/properties/list/properties-list-page').then(
    (m) => ({
      default: m.PropertiesListPage,
    }),
  ),
);

const ReminderAddPage = lazy(() =>
  import('~app/pages/lessor/reminders/add/reminder-add-page').then((m) => ({
    default: m.ReminderAddPage,
  })),
);

const ReminderEditPage = lazy(() =>
  import('~app/pages/lessor/reminders/edit/reminder-edit-page').then((m) => ({
    default: m.ReminderEditPage,
  })),
);

const RemindersListPage = lazy(() =>
  import('~app/pages/lessor/reminders/list/reminders-list-page').then((m) => ({
    default: m.RemindersListPage,
  })),
);

const TenantAddPage = lazy(() =>
  import('~app/pages/lessor/tenants/add/tenant-add-page').then((m) => ({
    default: m.TenantAddPage,
  })),
);

const TenantEditPage = lazy(() =>
  import('~app/pages/lessor/tenants/edit/tenant-edit-page').then((m) => ({
    default: m.TenantEditPage,
  })),
);

const TenantItemPage = lazy(() =>
  import('~app/pages/lessor/tenants/item/tenant-item-page').then((m) => ({
    default: m.TenantItemPage,
  })),
);

const TenantsListPage = lazy(() =>
  import('~app/pages/lessor/tenants/list/tenants-list-page').then((m) => ({
    default: m.TenantsListPage,
  })),
);

const UserItemPage = lazy(() =>
  import('~app/pages/lessor/user/item/user-item-page').then((m) => ({
    default: m.UserItemPage,
  })),
);

const SupportPage = lazy(() =>
  import('~app/pages/lessor/support/support-page').then((m) => ({
    default: m.SupportPage,
  })),
);

const TenantSupportPage = lazy(() =>
  import('~app/pages/tenant/support/tenant-support-page').then((m) => ({
    default: m.TenantSupportPage,
  })),
);

const AdminUsersListPage = lazy(() =>
  import('~app/pages/admin/users/list/admin-users-list-page').then((m) => ({
    default: m.AdminUsersListPage,
  })),
);

const AdminUserItemPage = lazy(() =>
  import('~app/pages/admin/users/item/admin-user-item-page').then((m) => ({
    default: m.AdminUserItemPage,
  })),
);

const AdminOutreachPage = lazy(() =>
  import('~app/pages/admin/outreach/admin-outreach-page').then((m) => ({
    default: m.AdminOutreachPage,
  })),
);

const AdminOutreachItemPage = lazy(() =>
  import('~app/pages/admin/outreach/item/admin-outreach-item-page').then(
    (m) => ({
      default: m.AdminOutreachItemPage,
    }),
  ),
);

const AdminCommEventsListPage = lazy(() =>
  import('~app/pages/admin/comm-events/list/admin-comm-events-list-page').then(
    (m) => ({
      default: m.AdminCommEventsListPage,
    }),
  ),
);

const AdminCommEventItemPage = lazy(() =>
  import('~app/pages/admin/comm-events/item/admin-comm-event-item-page').then(
    (m) => ({
      default: m.AdminCommEventItemPage,
    }),
  ),
);

const AdminInvoicesListPage = lazy(() =>
  import('~app/pages/admin/invoices/admin-invoices-list-page').then((m) => ({
    default: m.AdminInvoicesListPage,
  })),
);

const AdminPaymentsListPage = lazy(() =>
  import('~app/pages/admin/payments/admin-payments-list-page').then((m) => ({
    default: m.AdminPaymentsListPage,
  })),
);

const AdminLeasesListPage = lazy(() =>
  import('~app/pages/admin/leases/admin-leases-list-page').then((m) => ({
    default: m.AdminLeasesListPage,
  })),
);

const AdminPropertiesListPage = lazy(() =>
  import('~app/pages/admin/properties/admin-properties-list-page').then(
    (m) => ({
      default: m.AdminPropertiesListPage,
    }),
  ),
);

const AdminPartnerApplicationsPage = lazy(() =>
  import(
    '~app/pages/admin/partner-applications/admin-partner-applications-page'
  ).then((m) => ({
    default: m.AdminPartnerApplicationsPage,
  })),
);

const TenantLoginPage = lazy(() =>
  import('~app/pages/tenant/auth/login/tenant-login-page').then((m) => ({
    default: m.TenantLoginPage,
  })),
);

const TenantConnectPage = lazy(() =>
  import('~app/pages/tenant/connect/tenant-connect-page').then((m) => ({
    default: m.TenantConnectPage,
  })),
);

const TenantDashboardPage = lazy(() =>
  import('~app/pages/tenant/dashboard/tenant-dashboard-page').then((m) => ({
    default: m.TenantDashboardPage,
  })),
);

const TenantInvoiceItemPage = lazy(() =>
  import('~app/pages/tenant/invoices/item/tenant-invoice-item-page').then(
    (m) => ({
      default: m.TenantInvoiceItemPage,
    }),
  ),
);

const TenantInvoicesListPage = lazy(() =>
  import('~app/pages/tenant/invoices/list/tenant-invoices-list-page').then(
    (m) => ({
      default: m.TenantInvoicesListPage,
    }),
  ),
);

const TenantLeaseItemPage = lazy(() =>
  import('~app/pages/tenant/leases/item/tenant-lease-item-page').then((m) => ({
    default: m.TenantLeaseItemPage,
  })),
);

const TenantLeasesListPage = lazy(() =>
  import('~app/pages/tenant/leases/list/tenant-leases-list-page').then((m) => ({
    default: m.TenantLeasesListPage,
  })),
);

const TenantPaymentRecordAddPage = lazy(() =>
  import(
    '~app/pages/tenant/payments-records/add/tenant-payment-record-add-page'
  ).then((m) => ({
    default: m.TenantPaymentRecordAddPage,
  })),
);

const TenantPaymentRecordItemPage = lazy(() =>
  import(
    '~app/pages/tenant/payments-records/item/tenant-payment-record-item-page'
  ).then((m) => ({
    default: m.TenantPaymentRecordItemPage,
  })),
);

const TenantPaymentsRecordsListPage = lazy(() =>
  import(
    '~app/pages/tenant/payments-records/list/tenant-payments-records-list-page'
  ).then((m) => ({
    default: m.TenantPaymentsRecordsListPage,
  })),
);

const TenantPropertiesListPage = lazy(() =>
  import('~app/pages/tenant/properties/list/tenant-properties-list-page').then(
    (m) => ({
      default: m.TenantPropertiesListPage,
    }),
  ),
);

const TenantRemindersListPage = lazy(() =>
  import('~app/pages/tenant/reminders/list/tenant-reminders-list-page').then(
    (m) => ({
      default: m.TenantRemindersListPage,
    }),
  ),
);

export const routerConfig = createBrowserRouter([
  {
    element: <BaseRoute />,
    errorElement: <RouteErrorPage />,
    children: [
      {
        element: <LessorPrivateRoute />,
        children: [
          {
            element: <AdminPrivateRoute />,
            children: [
              {
                element: <AdminLayout />,
                children: [
                  {
                    path: ROUTES.ADMIN,
                    element: <Navigate to={ROUTES.ADMIN_USERS} replace />,
                  },
                  { path: ROUTES.ADMIN_USERS, element: <AdminUsersListPage /> },
                  { path: ROUTES.ADMIN_USER, element: <AdminUserItemPage /> },
                  {
                    path: ROUTES.ADMIN_OUTREACH,
                    element: <AdminOutreachPage />,
                  },
                  {
                    path: ROUTES.ADMIN_OUTREACH_ITEM,
                    element: <AdminOutreachItemPage />,
                  },
                  {
                    path: ROUTES.ADMIN_COMM_EVENTS,
                    element: <AdminCommEventsListPage />,
                  },
                  {
                    path: ROUTES.ADMIN_COMM_EVENT,
                    element: <AdminCommEventItemPage />,
                  },
                  {
                    path: ROUTES.ADMIN_INVOICES,
                    element: <AdminInvoicesListPage />,
                  },
                  {
                    path: ROUTES.ADMIN_PAYMENTS,
                    element: <AdminPaymentsListPage />,
                  },
                  {
                    path: ROUTES.ADMIN_LEASES,
                    element: <AdminLeasesListPage />,
                  },
                  {
                    path: ROUTES.ADMIN_PROPERTIES,
                    element: <AdminPropertiesListPage />,
                  },
                  {
                    path: ROUTES.ADMIN_PARTNER_APPLICATIONS,
                    element: <AdminPartnerApplicationsPage />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTES.ONBOARDING,
            element: <LessorOnboardingPage />,
          },
          {
            element: <LessorOnboardingRoute />,
            children: [
              {
                element: <LessorLayout />,
                children: [
                  { path: ROUTES.DASHBOARD, element: <DashboardPage /> },
                  { path: ROUTES.PROPERTIES, element: <PropertiesListPage /> },
                  { path: ROUTES.NEW_PROPERTY, element: <PropertyAddPage /> },
                  { path: ROUTES.PROPERTY, element: <PropertyItemPage /> },
                  { path: ROUTES.EDIT_PROPERTY, element: <PropertyEditPage /> },

                  { path: ROUTES.LEASES, element: <LeasesListPage /> },
                  { path: ROUTES.LEASE, element: <LeaseItemPage /> },
                  { path: ROUTES.EDIT_LEASE, element: <LeaseEditPage /> },
                  { path: ROUTES.NEW_LEASE, element: <LeaseAddPage /> },
                  {
                    path: ROUTES.LEASE_CREATED,
                    element: <LeaseItemCreatedPage />,
                  },

                  { path: ROUTES.INVOICES, element: <InvoicesListPage /> },
                  { path: ROUTES.INVOICE, element: <InvoiceItemPage /> },
                  { path: ROUTES.EDIT_INVOICE, element: <InvoiceEditPage /> },
                  { path: ROUTES.NEW_INVOICE, element: <InvoiceAddPage /> },

                  { path: ROUTES.TENANTS, element: <TenantsListPage /> },
                  { path: ROUTES.TENANT, element: <TenantItemPage /> },
                  { path: ROUTES.EDIT_TENANT, element: <TenantEditPage /> },
                  { path: ROUTES.NEW_TENANT, element: <TenantAddPage /> },

                  {
                    path: ROUTES.PAYMENTS_RECORDS,
                    element: <PaymentsRecordsListPage />,
                  },
                  {
                    path: ROUTES.PAYMENT_RECORD,
                    element: <PaymentRecordItemPage />,
                  },
                  {
                    path: ROUTES.EDIT_PAYMENT_RECORD,
                    element: <PaymentRecordEditPage />,
                  },
                  {
                    path: ROUTES.NEW_PAYMENT_RECORD,
                    element: <PaymentRecordAddPage />,
                  },

                  { path: ROUTES.REMINDERS, element: <RemindersListPage /> },
                  { path: ROUTES.NEW_REMINDER, element: <ReminderAddPage /> },
                  { path: ROUTES.EDIT_REMINDER, element: <ReminderEditPage /> },

                  { path: ROUTES.USER, element: <UserItemPage /> },
                  // { path: ROUTES.SUPPORT, element: <TenantSupportPage /> },
                  { path: ROUTES.SUPPORT, element: <SupportPage /> },
                ],
              },
            ],
          },
          {
            path: ROUTES.VERIFY_EMAIL,
            element: <VerifyEmailPage />,
          },
        ],
      },
      {
        element: <TenantPrivateRoute />,
        children: [
          {
            element: <TenantLayout />,
            children: [
              { path: ROUTES.TENANT_SCOPE, element: <TenantDashboardPage /> },
              {
                path: ROUTES.TENANT_PROPERTIES,
                element: <TenantPropertiesListPage />,
              },
              { path: ROUTES.TENANT_LEASES, element: <TenantLeasesListPage /> },
              { path: ROUTES.TENANT_LEASE, element: <TenantLeaseItemPage /> },
              {
                path: ROUTES.TENANT_REMINDERS,
                element: <TenantRemindersListPage />,
              },
              {
                path: ROUTES.TENANT_INVOICE,
                element: <TenantInvoiceItemPage />,
              },
              {
                path: ROUTES.TENANT_INVOICES,
                element: <TenantInvoicesListPage />,
              },
              {
                path: ROUTES.TENANT_PAYMENT,
                element: <TenantPaymentRecordItemPage />,
              },
              {
                path: ROUTES.TENANT_PAYMENTS,
                element: <TenantPaymentsRecordsListPage />,
              },
              {
                path: ROUTES.TENANT_NEW_PAYMENT,
                element: <TenantPaymentRecordAddPage />,
              },
              { path: ROUTES.TENANT_SUPPORT, element: <TenantSupportPage /> },
            ],
          },
        ],
      },
      {
        element: <LessorGuestOnlyRoute />,
        children: [
          { path: ROUTES.LOGIN, element: <LoginPage /> },
          { path: ROUTES.SIGN_UP, element: <SignupPage /> },
          { path: ROUTES.FORGOT_PASSWORD, element: <ForgotPasswordPage /> },
          { path: ROUTES.RESET_PASSWORD, element: <ResetPasswordPage /> },
        ],
      },
      {
        element: <TenantGuestOnlyRoute />,
        children: [{ path: ROUTES.TENANT_LOGIN, element: <TenantLoginPage /> }],
      },
      { path: ROUTES.TENANT_CONNECT, element: <TenantConnectPage /> },
      { path: ROUTES.OAUTH_SUCCESS, element: <OAuthSuccessPage /> },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
